import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Route, Routes, useNavigate, useLocation } from "react-router-dom";
import {
  BookOpen, Calendar, ChevronRight, CloudUpload, Code, Lightbulb, MessageSquare,
  ExternalLink, MoreHorizontal, Plug, Plus, RefreshCw, Search, Server, Store, Trash2, User, UsersRound, Zap,
} from "lucide-react";
import CreateTemplate from "./Create";
import EditTemplate from "./Edit";
import { deleteTemplate, getTemplates } from "../../services/templates";
import {
  getMCPServers, createMCPServer, deleteMCPServer, getMCPServerTools, startMCPOAuth,
} from "../../services/mcp-server";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import AdminLayout from "../../components/admin-layout";
import UserContext from "../../context/users";
import { AdminRole } from "../../constants";
import useDebounce from "../../hooks/useDebounce";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { ConfirmDialog, Dialog, DialogHeader, DialogContent, DialogFooter } from "../../components/ui/dialog";
import { DropdownMenu } from "../../components/ui/dropdown-menu";
import { EmptyState } from "../../components/ui/empty-state";
import { Pagination } from "../../components/ui/pagination";
import { Tooltip } from "../../components/ui/tooltip";
import { Spinner } from "../../components/ui/spinner";
import { TabsList } from "../../components/ui/tabs";
import { FormField, FormItem, FormLabel, FormMessage } from "../../components/ui/form-field";
import { cn } from "../../lib/cn";

const mcpServerSchema = z.object({
  name: z.string().min(1, "Nome é obrigatório").max(100, "Máximo 100 caracteres"),
  url: z.string().url("URL inválida").min(1, "URL é obrigatória"),
  transport: z.string().default("streamable"),
  authType: z.string().default("none"),
  authSecret: z.string().optional(),
  oauthClientID: z.string().optional(),
  oauthClientSecret: z.string().optional(),
  oauthTokenURL: z.string().optional(),
  oauthAuthorizationURL: z.string().optional(),
  oauthScope: z.string().optional(),
});

function TabbedPage({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  const currentTab = location.pathname.includes("/mcp-servers") ? "mcp" : "templates";

  const tabs = [
    { key: "templates", label: "Templates de Gatilho", icon: Zap },
    { key: "mcp", label: "MCP Servers", icon: Plug },
  ];

  const onTabChange = (key) => {
    if (key === "mcp") navigate("/templates/mcp-servers");
    else navigate("/templates");
  };

  return (
    <div className="dash-scroll flex h-full w-full flex-col overflow-y-auto bg-[var(--dash-bg)]">
      <div className="mx-auto w-full max-w-[1400px] px-6 pt-8">
        <TabsList items={tabs} value={currentTab} onChange={onTabChange} />
      </div>
      <div className="mx-auto w-full max-w-[1400px] px-6 py-6">
        {children}
      </div>
    </div>
  );
}

export default function TriggerTemplates() {
  return (
    <Routes>
      <Route path="/" element={
        <AdminLayout noPadding withBackground={false}>
          <TabbedPage><TemplateList /></TabbedPage>
        </AdminLayout>
      } />
      <Route path="/mcp-servers" element={
        <AdminLayout noPadding withBackground={false}>
          <TabbedPage><MCPServerList /></TabbedPage>
        </AdminLayout>
      } />
      <Route path="/create" element={
        <AdminLayout>
          <CreateTemplate />
        </AdminLayout>
      } />
      <Route path="/:id" element={
        <AdminLayout>
          <EditTemplate />
        </AdminLayout>
      } />
    </Routes>
  );
}

const ACTION_CONFIG = {
  WEBHOOK: { Icon: CloudUpload, label: "Webhook", color: "text-blue-400" },
  KNOWLEDGE: { Icon: Search, label: "Conhecimento", color: "text-amber-400" },
  CHAT_INTEGRATION: { Icon: MessageSquare, label: "Chat", color: "text-green-400" },
  GOOGLE_CALENDAR: { Icon: Calendar, label: "Google Agenda", color: "text-red-400" },
  CATALOG_INTEGRATION: { Icon: Store, label: "Catálogo", color: "text-cyan-400" },
  NATIVE: { Icon: Lightbulb, label: "Nativo", color: "text-yellow-400" },
  CRM: { Icon: User, label: "CRM", color: "text-pink-400" },
  FEEGOW: { Icon: UsersRound, label: "Feegow", color: "text-orange-400" },
};

function TemplateList() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const prevSearchRef = useRef(search);
  const navigate = useNavigate();

  const fetchList = useCallback(
    (p, pp, s) => {
      setLoading(true);
      getTemplates({ page: p, perPage: pp, search: s, searchField: "name" })
        .then(({ data: list, total: t }) => { setData(list || []); setTotal(t || 0); })
        .catch(() => toast.error("Falha ao carregar templates"))
        .finally(() => setLoading(false));
    },
    []
  );

  useEffect(() => {
    const isNew = prevSearchRef.current !== debouncedSearch;
    if (isNew) { prevSearchRef.current = debouncedSearch; setPage(1); }
    if (isNew && page !== 1) return;
    fetchList(page, perPage, debouncedSearch);
  }, [debouncedSearch, page, perPage, fetchList]);

  return (
    <div className="dash-scroll flex h-full w-full flex-col overflow-y-auto bg-[var(--dash-bg)]">
      <div className="mx-auto w-full max-w-[1400px] px-6 py-8">
        <header className="mb-6 flex flex-col gap-5">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="text-2xl font-semibold tracking-tight text-[var(--dash-text)]">
                Templates de Gatilho
              </h1>
              <p className="text-xs text-[var(--dash-text-muted)]">
                {total} template{total !== 1 ? "s" : ""}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-60">
                <Input
                  leftIcon={Search}
                  placeholder="Buscar..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <a href="https://verboo.ai/pt/docs/concepts/templates" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" leftIcon={BookOpen}>Docs</Button>
              </a>
              <Button variant="primary" leftIcon={Plus} onClick={() => navigate("create")}>
                Criar
              </Button>
            </div>
          </div>
        </header>

        {loading && data.length === 0 ? (
          <TemplateSkeleton />
        ) : data.length === 0 ? (
          <div className="rounded-2xl border border-[var(--dash-border)] bg-[var(--dash-surface)]">
            <EmptyState
              icon={Zap}
              title={debouncedSearch ? "Nenhum resultado" : "Crie seu primeiro template"}
              description={
                debouncedSearch
                  ? `Nada encontrado para "${debouncedSearch}".`
                  : "Templates padronizam o uso de gatilhos nos seus assistentes."
              }
              action={
                !debouncedSearch && (
                  <Button variant="primary" size="lg" leftIcon={Plus} onClick={() => navigate("create")}>
                    Criar primeiro template
                  </Button>
                )
              }
            />
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-[var(--dash-border)] bg-[var(--dash-surface)]">
            <div className="hidden items-center gap-4 border-b border-[var(--dash-border)] px-5 py-3 text-[10px] font-medium uppercase tracking-[0.12em] text-[var(--dash-text-muted)] md:flex">
              <div className="flex-1">Template</div>
              <div className="w-48">Ações</div>
              <div className="w-20 text-center">Params</div>
              <div className="w-8" />
            </div>

            <div className="divide-y divide-[var(--dash-border)]">
              {data.map((item, i) => (
                <TemplateRow
                  key={item.id}
                  item={item}
                  index={i}
                  onDeleted={() => fetchList(page, perPage, debouncedSearch)}
                />
              ))}
            </div>
          </div>
        )}

        {total > perPage && (
          <Pagination page={page} pageSize={perPage} total={total} onPageChange={setPage} />
        )}
      </div>
    </div>
  );
}

function TemplateRow({ item, index, onDeleted }) {
  const navigate = useNavigate();
  const [user] = useContext(UserContext);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isEditable = user.role === AdminRole || (item.companyID === user.companyID && item.isPublic);
  const paramCount = item?.args?.parameters?.properties
    ? Object.keys(item.args.parameters.properties).length
    : 0;

  const onDelete = useCallback(() => {
    setDeleting(true);
    deleteTemplate(item.id)
      .then(() => { toast.success("Template deletado!"); setConfirmDelete(false); onDeleted(); })
      .catch(() => toast.error("Falha ao deletar"))
      .finally(() => setDeleting(false));
  }, [item.id, onDeleted]);

  const menuItems = useMemo(() => {
    if (!isEditable) return [];
    return [
      { label: "Editar", icon: ChevronRight, onClick: () => navigate(item.id.toString()) },
      { separator: true },
      { label: "Deletar", icon: Trash2, danger: true, onClick: () => setConfirmDelete(true) },
    ];
  }, [isEditable, item.id, navigate]);

  return (
    <>
      <div
        className={cn(
          "group flex items-center gap-4 px-5 py-3.5 transition-colors",
          isEditable && "cursor-pointer hover:bg-[var(--dash-surface-hover)]"
        )}
        onClick={() => isEditable && navigate(item.id.toString())}
        style={{ animationDelay: `${index * 30}ms` }}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-[var(--dash-text)]">{item.name}</span>
            {item.isPublic && (
              <span className="inline-flex items-center gap-1 rounded-md bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-medium text-amber-400">
                <UsersRound size={9} /> Público
              </span>
            )}
          </div>
          {item.description && (
            <p className="truncate text-[11px] text-[var(--dash-text-muted)]">{item.description}</p>
          )}
          {/* Mobile-only badges */}
          <div className="flex flex-wrap items-center gap-1.5 pt-1 md:hidden">
            {item?.generativeActionTemplates?.map((a, i) => (
              <ActionBadge key={i} type={a.type} />
            ))}
            {paramCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-md bg-[var(--dash-primary-soft)] px-1.5 py-0.5 text-[9px] font-medium text-[var(--dash-primary)]">
                <Code size={9} /> {paramCount}
              </span>
            )}
          </div>
        </div>

        {/* Action type badges - desktop */}
        <div className="hidden w-48 md:flex">
          <div className="flex flex-wrap gap-1">
            {item?.generativeActionTemplates?.map((a, i) => (
              <ActionBadge key={i} type={a.type} />
            ))}
            {(!item?.generativeActionTemplates || item.generativeActionTemplates.length === 0) && (
              <span className="text-[11px] text-[var(--dash-text-faint)]">—</span>
            )}
          </div>
        </div>

        {/* Param count - desktop */}
        <div className="hidden w-20 md:flex justify-center">
          {paramCount > 0 ? (
            <span className="inline-flex items-center gap-1 rounded-md bg-[var(--dash-primary-soft)] px-2 py-0.5 text-[10px] font-medium text-[var(--dash-primary)]">
              <Code size={10} /> {paramCount}
            </span>
          ) : (
            <span className="text-[11px] text-[var(--dash-text-faint)]">—</span>
          )}
        </div>

        {/* Actions */}
        {isEditable && (
          <div className="w-8" onClick={(e) => e.stopPropagation()}>
            <DropdownMenu
              align="right"
              trigger={
                <button
                  type="button"
                  className="flex h-7 w-7 items-center justify-center rounded-lg text-[var(--dash-text-muted)] opacity-0 transition-all hover:bg-[var(--dash-surface-subtle)] group-hover:opacity-100"
                >
                  <MoreHorizontal size={14} />
                </button>
              }
              items={menuItems}
            />
          </div>
        )}

        {isEditable && (
          <ChevronRight
            size={13}
            className="shrink-0 text-[var(--dash-text-faint)] opacity-0 transition-opacity group-hover:opacity-100"
          />
        )}
      </div>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={onDelete}
        title="Deletar template?"
        description={`"${item.name}" será removido de todos os assistentes.`}
        confirmLabel="Deletar"
        loading={deleting}
      />
    </>
  );
}

function ActionBadge({ type }) {
  const config = ACTION_CONFIG[type];
  if (!config) return null;
  const { Icon, label, color } = config;
  return (
    <Tooltip content={label}>
      <span className={cn("inline-flex items-center gap-1 rounded-md bg-[var(--dash-surface-subtle)] px-1.5 py-0.5 text-[9px] font-medium ring-1 ring-[var(--dash-border)]", color)}>
        <Icon size={10} />
        <span className="hidden lg:inline text-[var(--dash-text-muted)]">{label}</span>
      </span>
    </Tooltip>
  );
}

export { ACTION_CONFIG };

// Compatibility export — used by Add-Templates modal (antd-based)
export function ActionType({ type }) {
  return <ActionBadge type={type} />;
}

const ROW_WIDTHS = [200, 160, 240, 180, 220, 150, 190, 170];

function Bone({ className, style, delay = 0 }) {
  return (
    <div
      className={cn("animate-pulse rounded-lg", className)}
      style={{ background: "var(--dash-surface-hover)", animationDelay: `${delay}ms`, ...style }}
    />
  );
}

function TemplateSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-[var(--dash-border)] bg-[var(--dash-surface)]">
      <div className="hidden items-center gap-4 border-b border-[var(--dash-border)] px-5 py-3 text-[10px] font-medium uppercase tracking-[0.12em] text-[var(--dash-text-faint)] md:flex">
        <div className="flex-1">Template</div>
        <div className="w-48">Ações</div>
        <div className="w-20 text-center">Params</div>
        <div className="w-8" />
      </div>
      <div className="divide-y divide-[var(--dash-border)]">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-5 py-4">
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <Bone className="h-4" delay={i * 60} style={{ width: ROW_WIDTHS[i % ROW_WIDTHS.length] }} />
              <Bone className="h-3 w-48" delay={i * 60 + 30} />
            </div>
            <div className="hidden w-48 gap-1.5 md:flex">
              <Bone className="h-5 w-14 !rounded-md" delay={i * 60 + 40} />
              <Bone className="h-5 w-14 !rounded-md" delay={i * 60 + 50} />
            </div>
            <div className="hidden w-20 md:flex justify-center">
              <Bone className="h-5 w-8 !rounded-md" delay={i * 60 + 60} />
            </div>
            <div className="w-8" />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   MCP SERVERS TAB
   ════════════════════════════════════════════════════════════════════ */

const AUTH_OPTIONS = [
  { label: "Nenhuma", value: "none" },
  { label: "Bearer Token", value: "bearer" },
  { label: "Headers Customizados", value: "header" },
  { label: "OAuth2 Login", value: "oauth2_mcp" },
];

const TRANSPORT_OPTIONS = [
  { label: "HTTP Streamable", value: "streamable" },
  { label: "SSE (legado)", value: "sse" },
];

const AUTH_LABELS = { none: "Sem auth", bearer: "Bearer", header: "Headers", oauth2_mcp: "OAuth2", oauth2_client_credentials: "OAuth2", oauth2_authorization_code: "OAuth2" };
const TRANSPORT_LABELS = { streamable: "Streamable", sse: "SSE" };

function MCPServerList() {
  const [loading, setLoading] = useState(true);
  const [servers, setServers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [toolsPreview, setToolsPreview] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchServers = useCallback(() => {
    setLoading(true);
    getMCPServers()
      .then(({ data }) => setServers(data || []))
      .catch(() => toast.error("Falha ao carregar MCP servers"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchServers(); }, [fetchServers]);

  const handleDelete = useCallback((id) => {
    setDeleting(true);
    deleteMCPServer(id)
      .then(() => { toast.success("Servidor removido!"); setConfirmDelete(null); fetchServers(); })
      .catch(() => toast.error("Falha ao remover servidor"))
      .finally(() => setDeleting(false));
  }, [fetchServers]);

  return (
    <>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <p className="text-xs text-[var(--dash-text-muted)]">
          {servers.length} servidor{servers.length !== 1 ? "es" : ""} configurado{servers.length !== 1 ? "s" : ""}
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" leftIcon={RefreshCw} onClick={fetchServers} disabled={loading}>Atualizar</Button>
          <Button variant="primary" leftIcon={Plus} onClick={() => setCreateOpen(true)}>Novo Servidor</Button>
        </div>
      </header>

      {loading && servers.length === 0 ? (
        <MCPSkeleton />
      ) : servers.length === 0 ? (
        <div className="rounded-2xl border border-[var(--dash-border)] bg-[var(--dash-surface)]">
          <EmptyState
            icon={Server}
            title="Nenhum servidor MCP"
            description="Conecte um servidor MCP para disponibilizar ferramentas externas nos seus assistentes."
            action={<Button variant="primary" size="lg" leftIcon={Plus} onClick={() => setCreateOpen(true)}>Criar primeiro servidor</Button>}
          />
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-[var(--dash-border)] bg-[var(--dash-surface)]">
          <div className="hidden items-center gap-4 border-b border-[var(--dash-border)] px-5 py-3 text-[10px] font-medium uppercase tracking-[0.12em] text-[var(--dash-text-muted)] md:flex">
            <div className="flex-1">Servidor</div>
            <div className="w-24 text-center">Transporte</div>
            <div className="w-24 text-center">Auth</div>
            <div className="w-24 text-center">Status</div>
            <div className="w-20" />
          </div>
          <div className="divide-y divide-[var(--dash-border)]">
            {servers.map((server, i) => (
              <MCPServerRow key={server.id} server={server} index={i} onPreviewTools={() => setToolsPreview(server)} onDelete={() => setConfirmDelete(server)} onRefresh={fetchServers} />
            ))}
          </div>
        </div>
      )}

      <CreateMCPServerDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); fetchServers(); }} />

      {toolsPreview && <ToolsPreviewDialog open server={toolsPreview} onClose={() => setToolsPreview(null)} />}

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => handleDelete(confirmDelete?.id)}
        title="Remover servidor MCP?"
        description={`"${confirmDelete?.name}" será desconectado de todos os assistentes.`}
        confirmLabel="Remover"
        loading={deleting}
      />
    </>
  );
}

function MCPServerRow({ server, index, onPreviewTools, onDelete, onRefresh }) {
  const usesOAuth = server.authType === "oauth2_authorization_code" || server.authType === "oauth2_mcp";
  const needsAuth = usesOAuth && !server.oauthAuthorized;

  const handleOAuth = () => {
    const popup = window.open("about:blank", "mcp_oauth", "width=600,height=700,popup=yes");
    if (!popup) {
      toast.error("Permita pop-ups para autorizar o servidor MCP.");
      return;
    }
    startMCPOAuth(server.id)
      .then(({ authorizationURL }) => {
        const target = new URL(authorizationURL);
        if (target.protocol !== "https:" && !(import.meta.env.DEV && target.protocol === "http:")) {
          throw new Error("unsafe OAuth authorization URL");
        }
        popup.location.replace(target.toString());
        const interval = setInterval(() => {
          if (popup.closed) {
            clearInterval(interval);
            onRefresh?.();
          }
        }, 500);
      })
      .catch(() => {
        popup.close();
        toast.error("Erro ao iniciar autorização OAuth.");
      });
  };

  return (
    <div className="group flex items-center gap-4 px-5 py-3.5 transition-colors hover:bg-[var(--dash-surface-hover)]" style={{ animationDelay: `${index * 30}ms` }}>
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl" style={{ background: "var(--dash-primary-soft)", border: "1px solid var(--dash-primary-border)", color: "var(--dash-primary)" }}>
          <Plug size={16} />
        </div>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-sm font-medium text-[var(--dash-text)]">{server.name}</span>
          <span className="truncate text-[11px] text-[var(--dash-text-muted)]">{server.url}</span>
        </div>
      </div>

      <div className="hidden w-24 md:flex justify-center">
        <Badge variant="info">{TRANSPORT_LABELS[server.transport] || "Streamable"}</Badge>
      </div>

      <div className="hidden w-24 md:flex justify-center">
        <Badge variant="default">{AUTH_LABELS[server.authType] || server.authType}</Badge>
      </div>

      <div className="hidden w-24 md:flex justify-center">
        {needsAuth ? (
          <Badge variant="warning" dot>Pendente</Badge>
        ) : (
          <Badge variant={server.active !== false ? "success" : "muted"} dot>{server.active !== false ? "Ativo" : "Inativo"}</Badge>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {usesOAuth && (
          <Tooltip content={needsAuth ? "Autorizar via OAuth" : "Reautorizar via OAuth"}>
            <button type="button" onClick={handleOAuth} className="flex h-7 w-7 items-center justify-center rounded-lg transition-all hover:bg-[var(--dash-primary-soft)]" style={{ color: "var(--dash-primary)" }}>
              <ExternalLink size={14} />
            </button>
          </Tooltip>
        )}
        <Tooltip content="Ver tools">
          <button type="button" onClick={onPreviewTools} className="flex h-7 w-7 items-center justify-center rounded-lg text-[var(--dash-text-muted)] transition-all hover:bg-[var(--dash-surface-subtle)] hover:text-[var(--dash-text)]">
            <Search size={14} />
          </button>
        </Tooltip>
        <Tooltip content="Remover">
          <button type="button" onClick={onDelete} className="flex h-7 w-7 items-center justify-center rounded-lg text-[var(--dash-text-muted)] transition-all hover:bg-[rgba(254,52,86,0.10)] hover:text-[#d61f3f]">
            <Trash2 size={14} />
          </button>
        </Tooltip>
      </div>
    </div>
  );
}

function CreateMCPServerDialog({ open, onClose, onCreated }) {
  const [saving, setSaving] = useState(false);
  const [headers, setHeaders] = useState([{ key: "", value: "" }]);

  const { control, handleSubmit, watch, reset, formState: { errors } } = useForm({
    resolver: zodResolver(mcpServerSchema),
    defaultValues: { name: "", url: "", transport: "streamable", authType: "none" },
    mode: "onBlur",
  });

  const authType = watch("authType");

  useEffect(() => { if (!open) { reset(); setHeaders([{ key: "", value: "" }]); } }, [open, reset]);

  const onSubmit = (data) => {
    const body = { ...data };
    if (data.authType === "header") {
      const obj = {};
      headers.filter((h) => h.key.trim()).forEach((h) => { obj[h.key.trim()] = h.value; });
      body.authHeaders = obj;
    }
    setSaving(true);
    createMCPServer(body)
      .then(() => { toast.success("Servidor MCP criado!"); onCreated(); })
      .catch(() => toast.error("Erro ao criar servidor MCP."))
      .finally(() => setSaving(false));
  };

  const addHeader = () => setHeaders((h) => [...h, { key: "", value: "" }]);
  const removeHeader = (i) => setHeaders((h) => h.filter((_, idx) => idx !== i));
  const setHeader = (i, field, val) => setHeaders((h) => h.map((item, idx) => idx === i ? { ...item, [field]: val } : item));

  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <DialogHeader icon={Server} title="Novo Servidor MCP" description="Configure a conexão com um servidor MCP externo." />
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <div className="flex flex-col gap-4">
            <button
              type="button"
              className="flex items-start gap-3 rounded-xl px-4 py-3 text-left transition-colors hover:bg-[var(--dash-primary-soft)]"
              style={{ background: "var(--dash-surface-subtle)", border: "1px solid var(--dash-primary-border)" }}
              onClick={() => reset({
                name: "Atendimento Verboo Code",
                url: "https://code.verboo.ai/support/mcp",
                transport: "streamable",
                authType: "oauth2_mcp",
              })}
            >
              <Plug size={17} className="mt-0.5 shrink-0 text-[var(--dash-primary)]" />
              <span className="flex flex-col gap-0.5">
                <span className="text-xs font-semibold text-[var(--dash-text)]">Usar preset de atendimento Verboo Code</span>
                <span className="text-[11px] text-[var(--dash-text-muted)]">Preenche o endpoint oficial e o OAuth restrito para autosserviço do cliente.</span>
              </span>
            </button>

            <FormField control={control} name="name" render={({ field, error }) => (
              <FormItem>
                <FormLabel required>Nome</FormLabel>
                <Input placeholder="Ex: Servidor de ferramentas" invalid={!!error} {...field} />
                <FormMessage error={error} />
              </FormItem>
            )} />

            <FormField control={control} name="url" render={({ field, error }) => (
              <FormItem>
                <FormLabel required>URL do servidor</FormLabel>
                <Input placeholder="https://mcp-server.example.com/mcp" invalid={!!error} {...field} />
                <FormMessage error={error} />
              </FormItem>
            )} />

            <div className="grid grid-cols-2 gap-3">
              <FormField control={control} name="transport" render={({ field }) => (
                <FormItem>
                  <FormLabel>Transporte</FormLabel>
                  <Select options={TRANSPORT_OPTIONS} value={field.value} onChange={field.onChange} />
                </FormItem>
              )} />
              <FormField control={control} name="authType" render={({ field }) => (
                <FormItem>
                  <FormLabel>Autenticação</FormLabel>
                  <Select options={AUTH_OPTIONS} value={field.value} onChange={field.onChange} />
                </FormItem>
              )} />
            </div>

            {authType === "bearer" && (
              <FormField control={control} name="authSecret" render={({ field }) => (
                <FormItem>
                  <FormLabel>Bearer Token</FormLabel>
                  <Input type="password" placeholder="Token de acesso" {...field} value={field.value || ""} />
                </FormItem>
              )} />
            )}

            {authType === "header" && (
              <div className="flex flex-col gap-2">
                <FormLabel>Headers de autenticação</FormLabel>
                {headers.map((h, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input placeholder="Header" value={h.key} onChange={(e) => setHeader(i, "key", e.target.value)} className="flex-1" />
                    <Input placeholder="Valor" value={h.value} onChange={(e) => setHeader(i, "value", e.target.value)} className="flex-1" />
                    {headers.length > 1 && (
                      <Button variant="ghost" size="icon-sm" type="button" onClick={() => removeHeader(i)}>
                        <Trash2 size={13} />
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" type="button" leftIcon={Plus} onClick={addHeader} className="self-start">
                  Adicionar header
                </Button>
              </div>
            )}

            {authType === "oauth2_mcp" && (
              <p className="rounded-xl px-4 py-3 text-[11px]" style={{ background: "var(--dash-primary-soft)", color: "var(--dash-primary)", border: "1px solid var(--dash-primary-border)" }}>
                Após criar o servidor, clique em "Autorizar" para fazer login no provedor OAuth. Nenhuma configuração extra é necessária.
              </p>
            )}
          </div>
        </DialogContent>
        <DialogFooter>
          <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" type="submit" loading={saving}>Criar Servidor</Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

function ToolsPreviewDialog({ open, server, onClose }) {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !server) return;
    setLoading(true);
    getMCPServerTools(server.id)
      .then(({ data }) => setTools(data || []))
      .catch(() => toast.error("Erro ao conectar ao servidor MCP."))
      .finally(() => setLoading(false));
  }, [open, server]);

  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <DialogHeader icon={Search} title={`Tools — ${server?.name}`} description={`${tools.length} tools disponíveis neste servidor`} />
      <DialogContent>
        {loading ? (
          <div className="flex items-center justify-center py-8"><Spinner size={20} /></div>
        ) : tools.length === 0 ? (
          <p className="py-6 text-center text-xs text-[var(--dash-text-muted)]">Nenhuma tool encontrada.</p>
        ) : (
          <div className="flex flex-col gap-1 max-h-[400px] overflow-y-auto pr-1">
            {tools.map((tool) => (
              <div key={tool.name} className="flex flex-col gap-0.5 rounded-xl px-4 py-3" style={{ background: "var(--dash-surface-subtle)" }}>
                <span className="text-xs font-medium text-[var(--dash-text)]">{tool.name}</span>
                {tool.description && <span className="text-[11px] leading-snug text-[var(--dash-text-muted)]">{tool.description}</span>}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
      <DialogFooter><Button variant="ghost" onClick={onClose}>Fechar</Button></DialogFooter>
    </Dialog>
  );
}

function Field({ label, description, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-[var(--dash-text-soft)]">{label}</label>
      {children}
      {description && <span className="text-[11px] text-[var(--dash-text-muted)]">{description}</span>}
    </div>
  );
}

function MCPSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-[var(--dash-border)] bg-[var(--dash-surface)]">
      <div className="hidden items-center gap-4 border-b border-[var(--dash-border)] px-5 py-3 text-[10px] font-medium uppercase tracking-[0.12em] text-[var(--dash-text-faint)] md:flex">
        <div className="flex-1">Servidor</div>
        <div className="w-24 text-center">Transporte</div>
        <div className="w-24 text-center">Auth</div>
        <div className="w-24 text-center">Status</div>
        <div className="w-16" />
      </div>
      <div className="divide-y divide-[var(--dash-border)]">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-5 py-3.5">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <Bone className="h-9 w-9 shrink-0 !rounded-xl" delay={i * 80} />
              <div className="flex min-w-0 flex-col gap-2">
                <Bone className="h-3.5 w-36" delay={i * 80 + 20} />
                <Bone className="h-3 w-52" delay={i * 80 + 40} />
              </div>
            </div>
            <div className="hidden w-24 md:flex justify-center"><Bone className="h-5 w-16 !rounded-full" delay={i * 80 + 50} /></div>
            <div className="hidden w-24 md:flex justify-center"><Bone className="h-5 w-14 !rounded-full" delay={i * 80 + 55} /></div>
            <div className="hidden w-24 md:flex justify-center"><Bone className="h-5 w-14 !rounded-full" delay={i * 80 + 60} /></div>
            <div className="flex w-20 items-center justify-end gap-1">
              <Bone className="h-7 w-7 !rounded-lg" delay={i * 80 + 70} />
              <Bone className="h-7 w-7 !rounded-lg" delay={i * 80 + 80} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
